import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../button";
import styles from "./style.module.css";

interface LogoutConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Подтверждение выхода из аккаунта (только web).
 * Мобайл (<768px) — bottom sheet снизу; десктоп — диалог по центру экрана.
 */
function LogoutConfirmModal({
  open,
  onClose,
  onConfirm,
  loading = false,
}: LogoutConfirmModalProps) {
  const [animatedOpen, setAnimatedOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setAnimatedOpen(false);
      return;
    }
    const frame = requestAnimationFrame(() => setAnimatedOpen(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, loading, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`${styles.overlay} ${animatedOpen ? styles.open : ""}`}
      role="presentation"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 id="logout-modal-title" className={styles.title}>
            Хотите выйти?
          </h2>
          <p className={styles.subtitle}>Подтвердите или отмените действие</p>
        </div>
        <div className={styles.actions}>
          <Button
            label="Выйти"
            onClick={onConfirm}
            disabled={loading}
            loading={loading}
          />
          <Button
            label="Отмена"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LogoutConfirmModal;
